import { logger } from '@springtree/eva-sdk-core-logger';
import { IEvaServiceCallOptions, bootstrapEndpoint } from '@springtree/eva-sdk-core-service';
import { Core, IEvaServiceDefinition } from '@springtree/eva-services-core';
// tslint:disable-next-line: import-name
import ky from 'ky-universal';
import { createLogic } from 'redux-logic';
import { IEvaReduxStoreDefaultShape } from '../store';
import {
  IEvaServiceErrorActionPayload,
  IEvaServiceRequestAction,
  IEvaServiceRequestActionPayload,
  IEvaServiceResponseActionPayload,
  IStateOptions,
} from '../action';
import { getServicePromise } from '../promise';
import { getStoreSettings } from '../settings';

// We need to recurse the child product arrays to find all stock holding products
//
const getStockHoldingProductIds = (products: EVA.Core.ConfigurableProductDto[]): number[] => {
  // Add the product id itself and recursively add the child item ids
  //
  const productIds: number[] = [];
  products.forEach((product) => {
    // Stock holding means not being configurable themselves
    if (product.Type !== 16384) {
      productIds.push(product.ProductID);
    }
    const childProductIds = getStockHoldingProductIds(product.Children || []);
    productIds.push(...childProductIds);
  });
  return productIds;
};

/**
 * The extended product details request for the composite helper.
 * You can specify the availability request options but the product id will be
 * set from the details.
 * Same goes for configurable and bundle details to set included fields.
 * Product prices requests has no additional properties but they are exposed
 * here to be future proof
 *
 * @export
 * @interface IExtendedProductDetailsRequest
 */
export interface IExtendedProductDetailsRequest {
  details: EVA.Core.GetProductDetail;
  availability?: Partial<EVA.Core.GetProductAvailability>;
  bundleDetails?: Partial<EVA.Core.GetBundleProductDetails>;
  configurableDetails?: Partial<EVA.Core.GetConfigurableProductDetail>;
}

/**
 * The extended product details response with properties filled depending
 * on the type of product returned.
 * Availability and prices can be toggled when creating the logic
 *
 * @export
 * @interface IExtendedProductDetailsResponse
 */
export interface IExtendedProductDetailsResponse {
  details: EVA.Core.GetProductDetailResponse;
  availability?: EVA.Core.GetProductAvailabilityResponse;
  bundleDetails?: EVA.Core.GetBundleProductDetailResponse;
  configurableDetails?: EVA.Core.GetConfigurableProductDetailResponse;
  prices?: EVA.Core.GetProductPricesResponse;
}

/**
 * The composite service definition for the ExtendedProductDetails helper
 *
 * @export
 * @class ExtendedProductDetails
 * @implements {IEvaServiceDefinition}
 */
export class ExtendedProductDetails implements IEvaServiceDefinition {
  name = 'Composite:ExtendedProductDetails';
  path = '/api/composite/ExtendedProductDetails';
  request?: IExtendedProductDetailsRequest;
  response?: IExtendedProductDetailsResponse;
}

/**
 * Additional options for the logic handling. Can be used to disable
 * additional request for prices and availability.
 *
 * @export
 * @interface IExtendedProductDetailsLogicOptions
 * @extends {IStateOptions}
 */
export interface IExtendedProductDetailsLogicOptions extends IStateOptions {
  /**
   * Availability is fetched by default but can be disabled with this option.
   * This options toggles availability for the product itself and all sellable
   * products that are configurable options or part of a bundle.
   *
   * @type {boolean}
   */
  disableAvailability?: boolean;

  /**
   * Product prices are fetched by default but can be disabled using this options
   *
   * @type {boolean}
   */
  disableProductPrices?: boolean;
}

/**
 * Composite service request logic builder for the ExtendedProductDetails helper
 *
 * @export
 * @template SHAPE
 * @param {IExtendedProductDetailsLogicOptions} [stateOptions]
 * @returns
 */
export function createExtendedProductDetailsLogic<SHAPE extends IEvaReduxStoreDefaultShape, SVC extends IEvaServiceDefinition = ExtendedProductDetails>(
  stateOptions?: IExtendedProductDetailsLogicOptions,
) {

  // Determine the state key name either by the service name or the provided
  // custom state key name
  //
  const serviceName = (stateOptions && stateOptions.stateKey) || new ExtendedProductDetails().name;

  const requestLogic = createLogic<SHAPE, IEvaServiceRequestAction<SVC>, any, any, any, string>({
    type: `${serviceName}:REQUEST`,
    processOptions: {
      dispatchReturn: true,
      successType: `${serviceName}:RESPONSE`,
      failType: `${serviceName}:ERROR`,
    },
    async process({ getState, action }) {
      const storeState: SHAPE = getState();
      const settings = getStoreSettings();
      const payload: IEvaServiceRequestActionPayload<ExtendedProductDetails> = action.payload;
      const endpointUrl = payload.endpoint || storeState.endpointUrl;
      const servicePromise = getServicePromise(payload.serviceContext);

      // Ensure we have an endpoint to use
      //
      if (!endpointUrl) {
        const failure = new Error('No EVA endpoint provided for service call');
        servicePromise.reject(failure);
        throw failure;
      }

      // Ensure we have a product identifier
      //
      if (!payload.data?.details?.ID) {
        const failure = new Error('Missing product ID for request');
        servicePromise.reject(failure);
        throw failure;
      }

      // Bootstrapping the store to get the EvaEndpoint instance we need
      // The SDK caches bootstrap results so after the first call this will be instant
      //
      const endpoint = await bootstrapEndpoint({ uri: endpointUrl });

      // Set provided chain context header
      //
      const serviceNameForProductDetail = new Core.GetProductDetail().name;
      const serviceOptions: IEvaServiceCallOptions = Object.assign(
        {
          chainContext: payload.chainContext,
        },
        payload.serviceOptions,
      );

      // Set the current user authentication token if needed
      //
      const currentUserToken = storeState.currentUserToken;
      if (!serviceOptions.authenticationToken && currentUserToken) {
        serviceOptions.authenticationToken = currentUserToken;
      }

      // Set the current application token if needed
      //
      const currentApplicationToken = storeState.currentApplicationToken;
      if (!serviceOptions.applicationToken && currentApplicationToken) {
        serviceOptions.applicationToken = currentApplicationToken;
      }

      // Call the product details service and return the response so the reducers can
      // write it to the store state
      //
      logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductDetail}`, payload, serviceOptions)();

      try {
        const currentProduct = await endpoint.callService(
          Core.GetProductDetail,
          payload.data.details,
          serviceOptions,
        ) as EVA.Core.GetProductDetailResponse;

        // Determine the product based on its type
        //
        const productId = payload.data.details.ID;
        const productTypes = currentProduct?.Result?.product_types || [];
        const isBundle = productTypes.indexOf(2048) !== -1;
        const isConfigurable = productTypes.indexOf(16384) !== -1;

        // Prepare the array that will contain all the involved product ids
        // These will be used to make single request for availability, prices, etc
        //
        const allProductIds = [productId];

        // Prepare the pickup organization unit(s) based on the current users
        // current organization
        //
        const organizationUnitsForPickup: number[] = [];
        const currentUser = storeState.currentUser?.data.response;
        if (currentUser?.User?.CurrentOrganizationID) {
          organizationUnitsForPickup.push(currentUser.User.CurrentOrganizationID);
        }

        // Prepare response so we can fill in all the optional type and availability
        //
        const responsePayload: IEvaServiceResponseActionPayload<ExtendedProductDetails> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: {
            details: currentProduct,
          },
        };

        if (isBundle) {
          const requestBundleDetails: EVA.Core.GetBundleProductDetails = {
            IncludedFields: settings.defaultProductProperties,
            ...payload.data?.bundleDetails,
            BundleProductID: productId,
          };

          const serviceNameForBundleProductDetails = new Core.GetBundleProductDetails().name;
          const serviceOptionsForBundleProductDetails: IEvaServiceCallOptions = Object.assign(
            {
              chainContext: payload.chainContext,
            },
            serviceOptions,
          );

          logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForBundleProductDetails}`, requestBundleDetails, serviceOptions)();
          (responsePayload.data as IExtendedProductDetailsResponse).bundleDetails = await endpoint.callService(
            Core.GetBundleProductDetails,
            requestBundleDetails,
            serviceOptionsForBundleProductDetails,
          );

          const bundleProducts = responsePayload.data?.bundleDetails?.Products ?? [];
          bundleProducts.forEach((bundleProduct) => {
            // Add the product ID for the product availability and prices request
            //
            allProductIds.push(bundleProduct.ID);
          });
        }

        if (isConfigurable) {
          const requestConfigurableDetails: EVA.Core.GetConfigurableProductDetail = {
            IncludedFields: settings.defaultProductProperties,
            ...payload.data?.configurableDetails,
            ID: productId,
          };

          const serviceNameForConfigurableProductDetails = new Core.GetConfigurableProductDetail().name;
          const serviceOptionsForConfigurableProductDetails: IEvaServiceCallOptions = Object.assign(
            {
              chainContext: payload.chainContext,
            },
            serviceOptions,
          );

          logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForConfigurableProductDetails}`, requestConfigurableDetails, serviceOptions)();
          const configurableDetails = await endpoint.callService(
            Core.GetConfigurableProductDetail,
            requestConfigurableDetails,
            serviceOptionsForConfigurableProductDetails,
          );
          (responsePayload.data as IExtendedProductDetailsResponse).configurableDetails = configurableDetails;

          // We will fetch availability for all stock holding products that are
          // an option for this configurable product
          //
          allProductIds.push(...getStockHoldingProductIds((configurableDetails as EVA.Core.GetConfigurableProductDetailResponse).Configurable.Children));
        }

        if (stateOptions?.disableAvailability !== true) {
          const requestAvailabilityPayload: EVA.Core.GetProductAvailability = {
            Options: {
              Bundles: {
                IncludeBundleInformation: true,
              },
              Delivery: {
                AvailabilityDate: true,
              },
              Pickup: {
                AvailabilityDate: true,
                OrganizationUnitIDs: organizationUnitsForPickup,
              },
              QuantityAvailable: true,
              ProductAvailabilityDetails: {
                OrganizationUnitIDs: organizationUnitsForPickup,
                ProductIDs: allProductIds,
              },
            },
            ...payload.data?.availability,

            // Override the product ids
            //
            Products: allProductIds.map((productId) => { return { ID: productId, QuantityRequested: 1 }; }),
          };

          const serviceNameForProductAvailability = new Core.GetProductAvailability().name;
          const serviceOptionsForProductAvailability: IEvaServiceCallOptions = Object.assign(
            {
              chainContext: payload.chainContext,
            },
            serviceOptions,
          );

          logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductAvailability}`, requestAvailabilityPayload, serviceOptions)();
          try {
            (responsePayload.data as IExtendedProductDetailsResponse).availability = await endpoint.callService(
              Core.GetProductAvailability,
              requestAvailabilityPayload,
              serviceOptionsForProductAvailability,
            );
          } catch (error) {
            logger.warn(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductAvailability} failed`, error)();
          }
        }

        if (stateOptions?.disableProductPrices !== true) {
          const requestProductPricesPayload: EVA.Core.GetProductPrices = {
            ProductIDs: allProductIds,
          };

          const serviceNameForProductPrices = new Core.GetProductPrices().name;
          const serviceOptionsForProductPrices: IEvaServiceCallOptions = Object.assign(
            {
              chainContext: payload.chainContext,
            },
            serviceOptions,
          );

          logger.debug(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductPrices}`, requestProductPricesPayload, serviceOptions)();
          try {
            (responsePayload.data as IExtendedProductDetailsResponse).prices = await endpoint.callService(
              Core.GetProductPrices,
              requestProductPricesPayload,
              serviceOptionsForProductPrices,
            );
          } catch (error) {
            logger.warn(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Call service ${serviceNameForProductPrices} failed`, error)();
          }
        }

        return responsePayload;
      } catch (error) {
        logger.error(`[EVA:SDK:LOGIC:COMPOSITE:${serviceName}] Service error`, error)();

        const errorPayload: IEvaServiceErrorActionPayload<ExtendedProductDetails> = {
          serviceContext: payload.serviceContext,
          chainContext: payload.chainContext,
          data: error.message,
        };

        // If this an HTTPError from fetch we can try to extract the JSON response
        //
        if (error instanceof ky.HTTPError) {
          try {
            const httpError: ky.HTTPError = error;
            const jsonError = await httpError.response.json();
            errorPayload.data = jsonError || error.message;
            errorPayload.httpResponse = error.response.status;
            const headers: Record<string, string> = {};
            error.response.headers.forEach((value, key) => (headers[key.toLowerCase()] = value));
            errorPayload.headers = headers;
          } catch (jsonParseError) {
            logger.warn(`[EVA:SDK:LOGIC:${serviceName}] Failed to parse JSON error response`, jsonParseError)();
          }
        }

        servicePromise.reject(errorPayload);
        throw errorPayload;
      }
    },
  });

  return [
    requestLogic,
  ];
}
